import { useCallback, useEffect, useState } from 'react';

import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import { type Endorsement, getEndorsements } from '../../api/getEndorsements';

type UseEndorsementsProps = {
	petitionSlugOrId: string;
	first?: number;
};

type UseEndorsementsReturn = ModelHookResult<
	{
		petitionId: string;
		endorsements: readonly Endorsement[];
		totalCount: number;
		isLoading: boolean;
		error: string | null;
	},
	{
		refetch: () => Promise<void>;
	}
>;

export function useEndorsements({ petitionSlugOrId, first = 10 }: UseEndorsementsProps): UseEndorsementsReturn {
	const utilityContext = useUtilityContext();
	const [petitionId, setPetitionId] = useState('');
	const [endorsements, setEndorsements] = useState<readonly Endorsement[]>([]);
	const [totalCount, setTotalCount] = useState(0);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const fetchEndorsements = useCallback(async () => {
		setIsLoading(true);
		setError(null);

		try {
			const response = await getEndorsements(petitionSlugOrId, first, utilityContext);
			setPetitionId(response.petitionId);
			setEndorsements(response.endorsements);
			setTotalCount(response.totalCount);
		} catch (e) {
			const message = e instanceof Error ? e.message : 'Failed to fetch endorsements';
			setError(message);
		} finally {
			setIsLoading(false);
		}
	}, [petitionSlugOrId, first, utilityContext]);

	useEffect(() => {
		void fetchEndorsements();
	}, [fetchEndorsements]);

	return {
		data: {
			petitionId,
			endorsements,
			totalCount,
			isLoading,
			error,
		},
		actions: {
			refetch: fetchEndorsements,
		},
	};
}
